import React, { useState, useEffect } from "react";
import { 
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Button, Dialog, 
  DialogTitle, DialogContent, DialogActions, TextField, Fab, Snackbar, Checkbox, FormControlLabel 
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CruzeTypeServices from "../services/externalServices/cruzeTypeServices";

const CruzeTypes = () => {
  const [cruzeTypes, setCruzeTypes] = useState([]);
  const [selectedCruzeType, setSelectedCruzeType] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false); // Confirmation dialog state
  const [selectedCruzeToDelete, setSelectedCruzeToDelete] = useState(null); // State to store the cruze type to be deleted
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [formData, setFormData] = useState({
    
    cruzeName: "",
    seatingCapacity: "",
    cruzeClass: "",
    thumbnailImage: "",
    cruzeType: "", // Added new field
    serviceAssociated:"",
    bodyType: "", // Added new field
    iconName: "", // Added new field
    priority: 0, // Added new field
    isEnabled: true, // Added the isEnabled field
  });

  // Fetch Cruze Types only once on mount
  useEffect(() => {
    fetchCruzeTypes();
  }, []); // Empty dependency array - this will only run once when the component mounts

  // Function to fetch Cruze Types
  async function fetchCruzeTypes() {
    try {
      const response = await CruzeTypeServices.getCruzeType();
      const sortedCruzeTypes = (response || []).sort((a, b) => a.priority - b.priority); // Sort by priority
      setCruzeTypes(sortedCruzeTypes); // Update the state with sorted data
    } catch (error) {
      console.log("Error while getting cruze types", error);
    }
  }

  // Handle Dialog Open/Close
  const handleDialogOpen = (cruze) => {
    if (cruze) {
      setSelectedCruzeType(cruze);
      setFormData(cruze);
    } else {
      setSelectedCruzeType(null);
      setFormData({
        cruzeName: "",
        seatingCapacity: "",
        serviceAssociated:"",
        cruzeClass: "",
        thumbnailImage: "",
        cruzeType: "", 
        bodyType: "", 
        iconName: "", 
        priority: 0, 
        isEnabled: true, 
      });
    }
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => setIsDialogOpen(false);

  // Handle Form Data Change
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle isEnabled Checkbox Change
  const handleIsEnabledChange = (e) => {
    setFormData({
      ...formData,
      isEnabled: e.target.checked,
    });
  };

  // Save Cruze Type (Add or Update)
  const handleSave = async () => {
    try {
      if (selectedCruzeType) {
        // Update existing Cruze Type
        await CruzeTypeServices.updateCruzeType(selectedCruzeType._id, formData);
        setSnackbarMessage("Cruze Type updated successfully!");
      } else {
        // Add new Cruze Type
        await CruzeTypeServices.addCruzeType(formData);
        setSnackbarMessage("Cruze Type added successfully!");
      }
      setIsDialogOpen(false);
      fetchCruzeTypes(); // Re-fetch the data after adding/updating
    } catch (error) {
      setSnackbarMessage("Error while saving Cruze Type!");
    }
  };

  // Open the Delete Confirmation Dialog
  const handleDeleteConfirmationOpen = (cruze) => {
    setSelectedCruzeToDelete(cruze); // Store the Cruze Type to delete
    setIsDeleteDialogOpen(true); // Open the confirmation dialog
  };

  // Close the Delete Confirmation Dialog
  const handleDeleteConfirmationClose = () => {
    setIsDeleteDialogOpen(false);
    setSelectedCruzeToDelete(null); // Clear the selected Cruze Type to delete
  };

  // Delete Cruze Type
  const handleDelete = async () => {
    if (selectedCruzeToDelete) {
      try {
        await CruzeTypeServices.deleteCruzeType(selectedCruzeToDelete._id);
        setSnackbarMessage("Cruze Type deleted successfully!");
        fetchCruzeTypes(); // Re-fetch the data after deleting
      } catch (error) {
        setSnackbarMessage("Error while deleting Cruze Type!");
      }
      setIsDeleteDialogOpen(false);
      setSelectedCruzeToDelete(null); // Clear the selected Cruze Type to delete
    }
  };

  return (
    <div style={{ marginTop: "30px" }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cruze Name</TableCell>
              <TableCell>Seating Capacity</TableCell>
              <TableCell>Service Associated</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Type</TableCell> {/* New Column */}
             <TableCell>Body Type</TableCell>
              <TableCell>Priority</TableCell> {/* New Column */}
              <TableCell>Enabled</TableCell> {/* New Column for isEnabled */}
              <TableCell>Image</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cruzeTypes.map((cruze) => (
              <TableRow key={cruze._id}>
                <TableCell>{cruze.cruzeName || "N/A"}</TableCell>
                <TableCell>{cruze.seatingCapacity || "N/A"}</TableCell>
                <TableCell>{cruze.serviceAssociated || "N/A"}</TableCell>
                <TableCell>{cruze.cruzeClass || "N/A"}</TableCell>
                <TableCell>{cruze.cruzeType || "N/A"}</TableCell> {/* New Column */}
                <TableCell>{cruze.bodyType || "N/A"}</TableCell> {/* New Column */}
                <TableCell>{cruze.priority || "N/A"}</TableCell> {/* New Column */}
                <TableCell>
                  <Checkbox
                    checked={cruze.isEnabled}
                    disabled
                  />
                </TableCell> {/* Displaying Enabled status */}
                <TableCell>
                  {cruze.thumbnailImage ? (
                    <img
                      src={cruze.thumbnailImage}
                      alt={cruze.cruzeName}
                      style={{ width: "50px", height: "50px" }}
                    />
                  ) : (
                    "N/A"
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleDialogOpen(cruze)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleDeleteConfirmationOpen(cruze)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>
          {selectedCruzeType ? "Edit Cruze Type" : "Add Cruze Type"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Cruze Name"
            name="cruzeName"
            value={formData.cruzeName}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Seating Capacity"
            name="seatingCapacity"
            value={formData.seatingCapacity}
            onChange={handleInputChange}
            type="number"
            fullWidth
            margin="dense"
          />
          <TextField
            label="Class"
            name="cruzeClass"
            value={formData.cruzeClass}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Cruze Type"
            name="cruzeType"
            value={formData.cruzeType}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
            <TextField
            label="Body Type"
            name="bodyType"
            value={formData.bodyType}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Service Associated"
            name="serviceAssociated"
            value={formData.serviceAssociated}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Icon Name"
            name="iconName"
            value={formData.iconName}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Priority"
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            type="number"
            fullWidth
            margin="dense"
          />
          <TextField
            label="Thumbnail Image URL"
            name="thumbnailImage"
            value={formData.thumbnailImage}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isEnabled}
                onChange={handleIsEnabledChange}
                name="isEnabled"
              />
            }
            label="Is Enabled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleDeleteConfirmationClose}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogActions>
          <Button onClick={handleDeleteConfirmationClose}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button (FAB) */}
      <Fab
        color="primary"
        aria-label="add"
        style={{ position: "fixed", bottom: "20px", right: "20px" }}
        onClick={() => handleDialogOpen(null)}
      >
        <AddIcon />
      </Fab>

      {/* Snackbar for Feedback */}
      <Snackbar
        open={!!snackbarMessage}
        message={snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage("")}
      />
    </div>
  );
};

export default CruzeTypes;
